// In src/pages/EmailSettingsPage.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Container, Typography, Box, Paper, TextField, Button,
  CircularProgress, Alert,
} from '@mui/material';

function EmailSettingsPage() {
  const { userProfile, churchProfile } = useAuth();
  const [smtpSettings, setSmtpSettings] = useState({
    server: '',
    port: '',
    user: '',
    password: '',
    fromName: '', // New state for the From Display Name
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Fetch existing settings when the component loads
  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }
    const settingsRef = doc(db, `churches/${userProfile.churchId}/secureConfiguration/smtpSettings`);
    getDoc(settingsRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSmtpSettings({
          server: data.server || '',
          port: data.port || '',
          user: data.user || '',
          fromName: data.fromName || '', // Load the From Name
          password: '', // Always keep password blank on load
        });
      }
    }).catch((err) => {
      console.error("Error fetching SMTP settings:", err);
      setError("Could not load existing settings.");
    }).finally(() => {
      setLoading(false);
    });
  }, [userProfile?.churchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSmtpSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Password is only required if it's being updated.
    if (!smtpSettings.server || !smtpSettings.port || !smtpSettings.user || !smtpSettings.fromName) {
      setError('All fields except Password are required.');
      setSaving(false);
      return;
    }

    try {
      const functions = getFunctions();
      const saveSmtpSettings = httpsCallable(functions, 'saveSmtpSettings');
      
      const payload = {
        churchId: userProfile.churchId,
        server: smtpSettings.server,
        port: smtpSettings.port,
        user: smtpSettings.user,
        fromName: smtpSettings.fromName,
      };

      // Only include the password in the payload if it was entered
      if (smtpSettings.password) {
        payload.password = smtpSettings.password;
      }

      const result = await saveSmtpSettings(payload);

      setSuccess(result.data.message);
      // Clear password field after successful save
      setSmtpSettings((prev) => ({ ...prev, password: '' }));
    } catch (err) {
      console.error("Error saving SMTP settings:", err);
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }
  
  if (!churchProfile?.enableEmailing) {
    return (
        <Container>
            <Alert severity="error">
                Email features are not enabled for your church. A Super Admin can enable this in Church Management.
            </Alert>
        </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
        <Typography variant="h4" gutterBottom>Email & SMTP Settings</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Configure the email server settings that will be used to send newsletters and notifications.
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="From Display Name (e.g., The Church Office)"
            name="fromName"
            value={smtpSettings.fromName}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="SMTP Server (e.g., smtp.gmail.com)"
            name="server"
            value={smtpSettings.server}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="SMTP Port (e.g., 587 or 465)"
            name="port"
            type="number"
            value={smtpSettings.port}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="SMTP Username (usually your full email address)"
            name="user"
            value={smtpSettings.user}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="SMTP Password"
            name="password"
            type="password"
            value={smtpSettings.password}
            onChange={handleChange}
            margin="normal"
            placeholder="Leave blank to keep existing password"
            helperText="For services like Gmail, you may need to generate an 'App Password'."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={saving}
            sx={{ mt: 2 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default EmailSettingsPage;